// src/sections/admin/CountersSection.js
import React, { useState } from 'react';
import axios from 'axios';
import ResourceTable from '../../components/admin/ResourceTable'; // Reusable table
import CounterForm from '../../components/admin/CounterForm';     // Counter form

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CountersSection = ({ user, services = [], counters = [], setMsg, fetchAll }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCounter, setEditingCounter] = useState(null);

  const adminTenantId = user?.tenant_id != null ? parseInt(user.tenant_id, 10) : null; 

  const counterColumns = [
    { key: 'id', label: 'ID' },
    { key: 'number', label: 'Counter No.' },
    { key: 'status', label: 'Status' },
    { 
      key: 'assigned_service_ids', 
      label: 'Assigned Services', 
      render: (assignedIds) => {
        if (!assignedIds || assignedIds.length === 0) return 'None';
        // Map assigned service IDs to their names and prefixes
        return assignedIds.map(id => {
          const service = services.find(s => s.id === id);
          return service ? `${service.name} (${service.prefix})` : `ID:${id}`;
        }).join(', ');
      }
    },
    { key: 'tenant_id', label: 'Tenant ID' }
  ];

  const handleAddCounter = () => {
    setEditingCounter(null); 
    setShowForm(true);
  };

  const handleEditCounter = (counterData) => {
    setEditingCounter(counterData);
    setShowForm(true);
  };

  const handleDeleteCounter = async (counterData) => {
    if (window.confirm(`Are you sure you want to delete counter ${counterData.number}? This will also remove its service assignments.`)) {
      try {
        await axios.delete(`${BASE_URL}/api/counters/${counterData.id}`);
        setMsg({ type: 'success', text: `Counter ${counterData.number} deleted successfully!` });
        fetchAll(); 
      } catch (error) {
        setMsg({ type: 'error', text: `Failed to delete counter: ${error.message}` });
        console.error("Error deleting counter:", error);
      }
    }
  };

  const handleSubmitCounterForm = async (formData, isEditMode) => {
    try {
      const dataToSubmit = { ...formData };
      
      // Filter out 'id' for new record creation, it's auto-generated
      if (!isEditMode) {
        delete dataToSubmit.id;
      }
      
      // Ensure tenant_id is correctly set for new counters
      dataToSubmit.tenant_id = adminTenantId;

      // Extract service assignment IDs for separate API calls
      const assignedServiceIds = dataToSubmit.assigned_service_ids;
      delete dataToSubmit.assigned_service_ids; // Remove from counter object for main CRUD

      let counterIdAfterSave;

      if (isEditMode) {
        // Update Counter
        await axios.patch(`${BASE_URL}/api/counters/${formData.id}`, dataToSubmit);
        counterIdAfterSave = formData.id;
        setMsg({ type: 'success', text: `Counter ${formData.number} updated successfully!` });
      } else {
        // Add Counter
        const res = await axios.post(`${BASE_URL}/api/counters`, dataToSubmit);
        counterIdAfterSave = res.data.id;
        setMsg({ type: 'success', text: `Counter ${formData.number} added successfully!` });
      }

      // --- CRITICAL: Manage Counter-Service Assignments ---
      // 1. Get current assignments for this counter from the overall 'counters' data
      const currentCounterRecord = counters.find(c => c.id === counterIdAfterSave);
      const existingAssignedServiceIds = currentCounterRecord?.assigned_service_ids || [];

      // 2. Determine assignments to ADD and REMOVE
      const servicesToAdd = assignedServiceIds.filter(id => !existingAssignedServiceIds.includes(id));
      const servicesToRemove = existingAssignedServiceIds.filter(id => !assignedServiceIds.includes(id));

      // 3. Execute API calls for adding/removing assignments
      const assignmentPromises = [];
      servicesToAdd.forEach(serviceId => {
        assignmentPromises.push(axios.post(`${BASE_URL}/api/counter_services`, { counter_id: counterIdAfterSave, service_id: serviceId }));
      });
      servicesToRemove.forEach(serviceId => {
        assignmentPromises.push(axios.delete(`${BASE_URL}/api/counter_services`, { data: { counter_id: counterIdAfterSave, service_id: serviceId } }));
      });

      await Promise.all(assignmentPromises); // Wait for all assignment updates to complete

      setMsg(prev => ({ type: 'success', text: prev.text + ' And service assignments updated.' }));

      setShowForm(false);
      setEditingCounter(null);
      fetchAll(); // Re-fetch all data to update the table including assignments
    } catch (error) {
      setMsg({ type: 'error', text: `Failed to ${isEditMode ? 'update' : 'add'} counter: ${error.message}` });
      console.error("Error submitting counter form:", error);
    }
  };

  // Patch: Only vendor_admin sees all, others see only own-tenant counters
  let filteredCounters = counters;
  if (user?.role === 'vendor_admin' || String(user?.tenant_id) === '999') {
    filteredCounters = counters;
  } else {
    filteredCounters = counters.filter(c => String(c.tenant_id) === String(adminTenantId));
  }

  return (
    <div>
      <h2 className="admin-section-header">Counter Management</h2>
      {!showForm ? (
        <>
          <button onClick={handleAddCounter} className="admin-button primary" style={{ marginBottom: '1.5rem' }}>Add New Counter</button>
          <ResourceTable 
            data={filteredCounters} 
            columns={counterColumns} 
            onEdit={handleEditCounter} 
            onDelete={handleDeleteCounter} 
            title="Counters"
          />
        </>
      ) : (
        <CounterForm 
          user={user} 
          initialData={editingCounter || {}} 
          services={services} // Pass services to the form for assignment options
          onSubmit={handleSubmitCounterForm} 
          onCancel={() => setShowForm(false)} 
        />
      )}
    </div>
  );
};

export default CountersSection;